import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GoogleMapReact from 'google-map-react';
import useSupercluster from 'use-supercluster';
import { useMapContext } from '../../context/MapContext';
import { filterMapChargersAlongRoute } from './utils/simpleRouteChargers';
import { getChargerAvailability } from './utils/availability';

const LIGHT_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eeeeee' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

const DARK_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#1b1b1b' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8c8c8c' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1b1b1b' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#3a3a3a' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#242424' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#8e8e8e' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a1a' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9a9a9a' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#b3b3b3' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f2f2f' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f2537' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4e6d8e' }] },
];

// Using shared availability utility from utils/availability.js

// Get network icon with status dot using pre-made assets - same as web app
const getNetworkIconWithStatus = (charger) => {
  if (!charger) return "/assets/networkFilter/Generic.svg";

  // Get possible network fields
  const possibleNetworkFields = [
    charger?.name,
    charger?.operator,
    charger?.chargepoint_name,
    charger?.source, 
    charger?.display_name,
    charger?.ownerName, 
    charger?.provider,
    charger?.owner,
    charger?.network,
    charger?.team_name,
  ].filter(Boolean);

  // Enhanced network mapping with status icon support
  const networkMapping = {
    // Indian Networks - SVG with status variants
    'zynka': { base: 'Zynka.png', hasStatus: false }, // Zynka doesn't have status variants
    // Suzuki / SMIPL variants
    'suzuki': { base: 'Suzuki.png', hasStatus: true },
    'smipl': { base: 'Suzuki.png', hasStatus: true },
    'sm ip l': { base: 'Suzuki.png', hasStatus: true },
    'maruti': { base: 'Suzuki.png', hasStatus: true },
    'statiq': { base: 'Statiq.svg', hasStatus: true },
    'tata': { base: 'Tata.svg', hasStatus: true },
    'tata power': { base: 'Tata.svg', hasStatus: true },
    'ather': { base: 'Ather.svg', hasStatus: true },
    'iocl': { base: 'IOCL.svg', hasStatus: true },
    'bpcl': { base: 'BPCL.svg', hasStatus: false },
    'hp': { base: 'HP.svg', hasStatus: false },
    'jio bp': { base: 'JioBP.svg', hasStatus: true },
    'jiobp': { base: 'JioBP.svg', hasStatus: true },
    'jio_bp': { base: 'JioBP.svg', hasStatus: true },
    'volttic': { base: 'Volttic.svg', hasStatus: true },
    'zeon': { base: 'Zeon.svg', hasStatus: true },
    'relux': { base: 'Relux.svg', hasStatus: true },
    'chargezone': { base: 'ChargeZone.svg', hasStatus: true },
    'charge zone': { base: 'ChargeZone.svg', hasStatus: true },
    'chargegrid': { base: 'ChargeGrid.svg', hasStatus: true },
    'charge grid': { base: 'ChargeGrid.svg', hasStatus: true },
    'ola': { base: 'Ola.svg', hasStatus: true },
    'eesl': { base: 'EESL.svg', hasStatus: true },
    'ntpc': { base: 'NTPC.svg', hasStatus: true },
    'pgcil': { base: 'PGCIL.svg', hasStatus: true },
    'reil': { base: 'REIL.svg', hasStatus: true },
    'tcil': { base: 'TCIL.svg', hasStatus: true },
    'becil': { base: 'BECIL.svg', hasStatus: true },
    'bbmp': { base: 'BBMP.svg', hasStatus: true },
    'delta': { base: 'Delta.svg', hasStatus: true },
    'efill': { base: 'Efill.svg', hasStatus: true },
    'electriva': { base: 'Electriva.svg', hasStatus: true },
    'evre': { base: 'EVRe.svg', hasStatus: true },
    'fortum': { base: 'Fortum.svg', hasStatus: true },
    'goego': { base: 'Goego.svg', hasStatus: true },
    'kazam': { base: 'Kazam.svg', hasStatus: true },
    'kht': { base: 'KHT.svg', hasStatus: true },
    'kropex': { base: 'Kropex.svg', hasStatus: true },
    'minooki': { base: 'Minooki.svg', hasStatus: true },
    'oem': { base: 'OEM.svg', hasStatus: true },
    'plugzmart': { base: 'PlugzMart.svg', hasStatus: true },
    'verde': { base: 'verde.svg', hasStatus: true },
    'xobolt': { base: 'XObolt.svg', hasStatus: true },
    'aargo': { base: 'AARgo.svg', hasStatus: true },
    
    // International Networks - PNG with Map icon prefix
    'tesla': { base: 'Tesla Public Supercharger.png', hasStatus: true, pngFormat: true, prefix: 'Tesla' },
    'fastned': { base: 'Fastned.png', hasStatus: true, pngFormat: true, prefix: 'Fastned' },
    'ionity': { base: 'Ionity.png', hasStatus: true, pngFormat: true, prefix: 'Ionity' },
    'chargepoint': { base: 'ChargePoint.png', hasStatus: true, pngFormat: true, prefix: 'ChargePoint' },
    'blink': { base: 'Blink.png', hasStatus: true, pngFormat: true, prefix: 'Blink' },
    'bp pulse': { base: 'BP Pulse.png', hasStatus: true, pngFormat: true, prefix: 'BP Pulse' },
    'shell recharge': { base: 'Community by Shell Recharge.png', hasStatus: true, pngFormat: true, prefix: 'shellrecharge' },
    'instavolt': { base: 'InstaVolt.png', hasStatus: true, pngFormat: true, prefix: 'INSTAVOLT' },
    'pod point': { base: 'Pod Point.png', hasStatus: true, pngFormat: true, prefix: 'POD' },
    'osprey': { base: 'Osprey.png', hasStatus: true, pngFormat: true, prefix: 'Osprey' },
    'geniepoint': { base: 'GeniePoint.png', hasStatus: true, pngFormat: true, prefix: 'GeniePoint' },
    'gridserve': { base: 'GRIDSERVE Electric Highway.png', hasStatus: true, pngFormat: true, prefix: 'GRIDDDSERVE' },
    'mer': { base: 'Mer.png', hasStatus: true, pngFormat: true, prefix: 'MER' },
    'mfg': { base: 'MFG EV Power.png', hasStatus: true, pngFormat: true, prefix: 'MFG' },
    'monta': { base: 'Monta.png', hasStatus: true, pngFormat: true, prefix: 'Monta' },
    'pogo': { base: 'PoGo.png', hasStatus: true, pngFormat: true, prefix: 'PoGo' },
    'raw charging': { base: 'RAW Charging.png', hasStatus: true, pngFormat: true, prefix: 'RAW' },
    'scottishpower': { base: 'ScottishPower Recharge.png', hasStatus: true, pngFormat: true, prefix: 'Scottish Power' },
    'source london': { base: 'Source London.png', hasStatus: true, pngFormat: true, prefix: 'Source London' },
    'ubitricity': { base: 'SR Ubitricity .png', hasStatus: true, pngFormat: true, prefix: 'SR Ubitricity' },
    'vendelectric': { base: 'VendElectric.png', hasStatus: true, pngFormat: true, prefix: 'Vend Electric' },
    'zapmap': { base: 'ZAPMAP.png', hasStatus: true, pngFormat: true, prefix: 'ZAPMAP' },
    'zeronet': { base: 'Zeronet.png', hasStatus: true, pngFormat: true, prefix: 'Zeronet' },
    'zest': { base: 'Zest.png', hasStatus: true, pngFormat: true, prefix: 'Zest' },
    'allego': { base: 'Allego.png', hasStatus: true, pngFormat: true, prefix: 'Allego' },
    'apcoa': { base: 'APCOA.png', hasStatus: true, pngFormat: true, prefix: 'APCOA' },
    'alfa power': { base: 'Alfa Power.png', hasStatus: true, pngFormat: true, prefix: 'Alfa_blue' },
    'chargy': { base: 'chargy.png', hasStatus: true, pngFormat: true, prefix: 'Char.gy' },
    'chargemy street': { base: 'ChargeMyStreet.png', hasStatus: true, pngFormat: true, prefix: 'Charge My Street' },
    'energise': { base: 'Energise.png', hasStatus: true, pngFormat: true, prefix: 'Energise' },
    'eo charging': { base: 'EO Charging.png', hasStatus: true, pngFormat: true, prefix: 'EO' },
    'esb energy': { base: 'ESB Energy.png', hasStatus: true, pngFormat: true, prefix: 'ESB Energy' },
    'evolt': { base: 'Evolt Network.png', hasStatus: true, pngFormat: true, prefix: 'Evolt' },
    'fuuse': { base: 'Fuuse.png', hasStatus: true, pngFormat: true, prefix: 'Fuuse' },
    'lidl': { base: 'Lidl.png', hasStatus: true, pngFormat: true, prefix: 'Lidl' },
    'justcharge': { base: 'JustCharge by JustPark.png', hasStatus: true, pngFormat: true, prefix: 'JustPark' },
    
    // Fallbacks
    'generic': { base: 'Generic.svg', hasStatus: true },
    'other': { base: 'Generic.svg', hasStatus: true }
  };

  // Try to match network name
  let detectedNetwork = 'generic';
  for (const networkField of possibleNetworkFields) {
    const normalizedField = networkField.toLowerCase().trim();
    
    // Direct mapping check
    if (networkMapping[normalizedField]) {
      detectedNetwork = normalizedField;
      break;
    }
    
    // Partial matching for complex names
    for (const [key] of Object.entries(networkMapping)) {
      if (normalizedField.includes(key) || key.includes(normalizedField)) {
        detectedNetwork = key;
        break;
      }
    }
    
    if (detectedNetwork !== 'generic') break;
  }

  const networkInfo = networkMapping[detectedNetwork] || networkMapping['generic'];
  const availabilityStatus = getChargerAvailability(charger);
  
  // Debug logging for specific chargers to track availability
  if (charger.name && (charger.name.includes('Savy Ville') || charger.name.includes('Shri Banke Bihari'))) {
  }
  
  // If no status or network doesn't have status variants, use base icon
  if (!availabilityStatus || !networkInfo.hasStatus) {
    const iconPath = `/assets/networkFilter/${networkInfo.base}`;
    return iconPath;
  }

  // Generate status icon path based on format
  let statusIconPath;
  if (networkInfo.pngFormat) {
    // PNG format: "Map icon_{prefix} {status}.png"
    const prefix = networkInfo.prefix || detectedNetwork;
    let statusSuffix;
    switch (availabilityStatus) {
      case 'available':
        statusSuffix = 'Green Dot';
        break;
      case 'in_use':
        statusSuffix = 'Grey'; // In use = grey dot
        break;
      case 'unavailable':
        statusSuffix = 'Red Dot';
        break;
      default:
        statusSuffix = 'Grey';
    }
    statusIconPath = `/assets/networkFilter/Map icon_${prefix} ${statusSuffix}.png`;
  } else {
    // SVG format: "NetworkName_{color}.svg"
    const baseNameWithoutExt = networkInfo.base.replace('.svg', '');
    let statusSuffix;
    switch (availabilityStatus) {
      case 'available':
        statusSuffix = 'green';
        break;
      case 'in_use':
        statusSuffix = 'grey'; // In use = grey dot
        break;
      case 'unavailable':
        statusSuffix = 'red';
        break;
      default:
        statusSuffix = 'grey';
    }
    statusIconPath = `/assets/networkFilter/${baseNameWithoutExt}_${statusSuffix}.svg`;
  }
  
  // Special logging for specific charger
  if (charger.name && charger.name.includes('Shri Banke Bihari')) {
  }
  
  return statusIconPath;
};

// Create 3D cluster icon with medium size and enhanced 3D effect
const create3DClusterIcon = (pointCount, totalPoints) => {
  // Medium size clusters - minimum 35px, maximum 65px based on count
  const baseSize = Math.max(35, Math.min(65, 30 + (pointCount * 5)));
  const radius = baseSize / 2;
  
  // Create SVG with enhanced 3D effect using gradients, shadows, and highlights
  const svg = `
    <svg width="${baseSize}" height="${baseSize}" viewBox="0 0 ${baseSize} ${baseSize}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <!-- Enhanced radial gradient for 3D effect -->
        <radialGradient id="clusterGradient${pointCount}" cx="35%" cy="35%">
          <stop offset="0%" style="stop-color:#8FE8FF;stop-opacity:1" />
          <stop offset="30%" style="stop-color:#2EBBFF;stop-opacity:1" />
          <stop offset="70%" style="stop-color:#4F6BFF;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#7F4BFF;stop-opacity:1" />
        </radialGradient>
        
        <!-- Highlight gradient for top shine -->
        <radialGradient id="highlight${pointCount}" cx="40%" cy="40%">
          <stop offset="0%" style="stop-color:#ffffff;stop-opacity:0.8" />
          <stop offset="50%" style="stop-color:#ffffff;stop-opacity:0.3" />
          <stop offset="100%" style="stop-color:#ffffff;stop-opacity:0" />
        </radialGradient>
        
        <!-- Enhanced shadow filter -->
        <filter id="shadow${pointCount}" x="-100%" y="-100%" width="300%" height="300%">
          <feDropShadow dx="3" dy="4" stdDeviation="4" flood-color="rgba(0,0,0,0.4)"/>
          <feDropShadow dx="1" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.2)"/>
        </filter>
        
        <!-- Inner shadow for depth -->
        <filter id="innerShadow${pointCount}" x="-50%" y="-50%" width="200%" height="200%">
          <feFlood flood-color="rgba(0,0,0,0.3)"/>
          <feComposite in="SourceGraphic"/>
        </filter>
      </defs>
      
      <!-- Main circle with 3D effect -->
      <circle 
        cx="${radius}" 
        cy="${radius}" 
        r="${radius - 4}" 
        fill="url(#clusterGradient${pointCount})" 
        stroke="#ffffff" 
        stroke-width="4"
        filter="url(#shadow${pointCount})"
      />
      
      <!-- Inner highlight circle for 3D depth -->
      <circle 
        cx="${radius - 3}" 
        cy="${radius - 3}" 
        r="${radius - 8}" 
        fill="url(#highlight${pointCount})" 
        opacity="0.6"
      />
      
      <!-- Count text with enhanced styling -->
      <text 
        x="${radius}" 
        y="${radius + 6}" 
        text-anchor="middle" 
        fill="white" 
        font-family="Arial, sans-serif" 
        font-size="${Math.max(16, Math.min(24, baseSize / 3.5))}" 
        font-weight="900"
        text-shadow="2px 2px 4px rgba(0,0,0,0.7)"
        stroke="rgba(0,0,0,0.3)"
        stroke-width="0.5"
      >
        ${pointCount}
      </text>
    </svg>
  `;
  
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(baseSize, baseSize),
    anchor: new window.google.maps.Point(radius, radius)
  };
};

// Create custom marker with dynamic network icon and co-location count
// Create custom marker with pre-made status icons - same as web app
const createCustomMarker = (charger, position, map, colocatedCount = 1, allChargers = []) => {
  const availabilityStatus = getChargerAvailability(charger);
  
  // Debug availability for specific chargers
  if (charger.name && (charger.name.includes('Savy Ville') || charger.name.includes('Shri Banke Bihari') || charger.name.includes('Spring House'))) {
  }
  
  // Get the appropriate icon (with status if available)
  const iconPath = getNetworkIconWithStatus(charger);
  
  const marker = new window.google.maps.Marker({
    position,
    map,
    icon: {
      url: iconPath,
      scaledSize: new window.google.maps.Size(40, 40), // Increased from 32x32 to 40x40
      anchor: new window.google.maps.Point(20, 20), // Adjusted anchor point
    },
    title: colocatedCount > 1 
      ? `${colocatedCount} charging stations at this location` 
      : (charger.name || charger.chargepoint_name || 'Charging Station'),
    optimized: false, // Important for custom icons and animations
    zIndex: colocatedCount > 1 ? 1100 : 1000, // Higher zIndex for multi-charger locations
    animation: null, // No drop animation - smooth appearance
  });

  // Add charger count overlay for co-located chargers (similar to clusters but smaller)
  if (colocatedCount > 1) {
    try {
      const countOverlay = new window.google.maps.OverlayView();
      countOverlay.onAdd = function() {
        try {
          const div = document.createElement('div');
          div.style.cssText = `
            position: absolute;
            background: #ff4444;
            color: white;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: bold;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            pointer-events: none;
            z-index: 1200;
          `;
          div.textContent = colocatedCount;
          this.div = div;
          this.getPanes().overlayMouseTarget.appendChild(div);
        } catch (error) {
        }
      };
      
      countOverlay.draw = function() {
        try {
          const projection = this.getProjection();
          const pos = projection.fromLatLngToDivPixel(position);
          if (pos && this.div) {
            this.div.style.left = (pos.x + 12) + 'px'; // Position to top-right of marker
            this.div.style.top = (pos.y - 8) + 'px';
          }
        } catch (error) {
        }
      };
      
      countOverlay.onRemove = function() {
        try {
          if (this.div) {
            this.div.parentNode.removeChild(this.div);
            this.div = null;
          }
        } catch (error) {
        }
      };
      
      countOverlay.setMap(map);
      
      // Store overlay reference for cleanup
      marker.countOverlay = countOverlay;
    } catch (error) {
    }
  }

  // Add smooth scale-in animation for individual markers
  setTimeout(() => {
    // Quick scale animation instead of drop
    const currentIcon = marker.getIcon();
    if (currentIcon && currentIcon.scaledSize) {
      // Start small and scale up
      marker.setIcon({
        ...currentIcon,
        scaledSize: new window.google.maps.Size(20, 20)
      });
      
      setTimeout(() => {
        marker.setIcon({
          ...currentIcon,
          scaledSize: new window.google.maps.Size(40, 40)
        });
      }, 100);
    }
  }, Math.random() * 100); // Staggered appearance

  // Add hover animations with scale effect
  marker.addListener('mouseover', () => {
    // Scale up on hover
    const currentIcon = marker.getIcon();
    if (currentIcon && currentIcon.scaledSize) {
      marker.setIcon({
        ...currentIcon,
        scaledSize: new window.google.maps.Size(45, 45),
        anchor: new window.google.maps.Point(22.5, 22.5)
      });
    }
  });

  marker.addListener('mouseout', () => {
    // Scale back to normal
    const currentIcon = marker.getIcon();
    if (currentIcon) {
      marker.setIcon({
        ...currentIcon,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20)
      });
    }
  });
  
  return marker;
};

const MapContainer = ({ 
  chargers = [], 
  onChargerClick, 
  onChargerHover,
  onChargerMouseOut = null,
  routeData = null,
  showRouteChargersOnly = false,
  onAddCheckpoint = null,
  isRouting = false,
  onAddToTrip = null,
  onRemoveFromTrip = null
}) => {
  const { 
    map, 
    setMap, 
    setMapsApi, 
    mapCenter, 
    mapZoom,
    markersRef,
    route,
    directionsRendererRef,
    chargers: mapChargers,
    filteredChargers
  } = useMapContext();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const mapStyles = useMemo(() => (isDarkMode ? DARK_MAP_STYLE : LIGHT_MAP_STYLE), [isDarkMode]);
  const mapLibraries = useMemo(() => ['places', 'geometry', 'drawing'], []);
  const bootstrapKeys = useMemo(
    () => ({
      key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      libraries: mapLibraries,
    }),
    [mapLibraries]
  );
  const mapOptions = useMemo(
    () => ({
      styles: mapStyles,
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      gestureHandling: 'greedy',
      scrollwheel: true,
    }),
    [mapStyles]
  );
  
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const routePolylineRef = useRef(null);
  const routeMarkersRef = useRef([]);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapZoomLevel, setMapZoomLevel] = useState(12);
  
  // Real-time status update interval
  const [lastStatusUpdate, setLastStatusUpdate] = useState(Date.now());

  // Add real-time status updates
  useEffect(() => {
    // Update status every 30 seconds for dynamic rendering
    const interval = setInterval(() => {
      setLastStatusUpdate(Date.now());
    }, 30000); // 30 seconds
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (map && isMapLoaded) {
      map.setOptions({ styles: mapStyles });
    }
  }, [map, isMapLoaded, mapStyles]);

  // Force re-render markers when status should update
  useEffect(() => {
    if (map && isMapLoaded && markersRef.current.length > 0) {
      // Re-render markers to update status colors
      setTimeout(() => {
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [lastStatusUpdate, map, isMapLoaded]); // Removed markersRef as it's a ref

  // Utility function to detect co-located chargers (same lat/lng within tolerance)
  const findColocatedChargers = useCallback((targetCharger, allChargers, tolerance = 0.0001) => {
    const targetLat = targetCharger.locationLatitude || targetCharger.location?.coordinates?.[1] || targetCharger.lat;
    const targetLng = targetCharger.locationLongitude || targetCharger.location?.coordinates?.[0] || targetCharger.lng;
    
    if (!targetLat || !targetLng) return [targetCharger];
    
    return allChargers.filter(charger => {
      const lat = charger.locationLatitude || charger.location?.coordinates?.[1] || charger.lat;
      const lng = charger.locationLongitude || charger.location?.coordinates?.[0] || charger.lng;
      
      if (!lat || !lng) return false;
      
      const latDiff = Math.abs(parseFloat(lat) - parseFloat(targetLat));
      const lngDiff = Math.abs(parseFloat(lng) - parseFloat(targetLng));
      
      return latDiff <= tolerance && lngDiff <= tolerance;
    });
  }, []);

  // Enhanced marker click handler for co-located chargers
  const handleMarkerClick = useCallback((charger, allChargers) => {
    const colocatedChargers = findColocatedChargers(charger, allChargers);
    
    
    if (colocatedChargers.length > 1) {
      // Show charger selection interface by passing the group
      if (onChargerClick) {
        onChargerClick(colocatedChargers[0], colocatedChargers); // First charger as primary, all as group
      }
    } else {
      if (onChargerClick) {
        onChargerClick(charger);
      }
    }
  }, [findColocatedChargers, onChargerClick]);

  const handleApiLoaded = useCallback(({ map, maps }) => {
    setMap(map);
    setMapsApi(maps);
    map.setOptions({ 
      styles: mapStyles,
      gestureHandling: 'greedy', // Enable scroll zoom without Ctrl key
    });
    setIsMapLoaded(true);
    
    
    // Listen to map changes
    map.addListener('zoom_changed', () => {
      const zoom = map.getZoom();
      setMapZoomLevel(zoom);
      // Force re-render of markers when zoom changes
      setTimeout(() => {
        const bounds = map.getBounds();
        if (bounds) {
          setMapBounds([
            bounds.getSouthWest().lng(),
            bounds.getSouthWest().lat(),
            bounds.getNorthEast().lng(),
            bounds.getNorthEast().lat()
          ]);
        }
      }, 100);
    });
    
    map.addListener('bounds_changed', () => {
      const bounds = map.getBounds();
      if (bounds) {
        const newBounds = [
          bounds.getSouthWest().lng(),
          bounds.getSouthWest().lat(),
          bounds.getNorthEast().lng(),
          bounds.getNorthEast().lat()
        ];
        setMapBounds(newBounds);
      }
    });
    
    // Set initial values with a small delay
    setTimeout(() => {
      setMapZoomLevel(map.getZoom());
      const bounds = map.getBounds();
      if (bounds) {
        setMapBounds([
          bounds.getSouthWest().lng(),
          bounds.getSouthWest().lat(),
          bounds.getNorthEast().lng(),
          bounds.getNorthEast().lat()
        ]);
      }
    }, 100);
  }, [mapStyles, setMap, setMapsApi, setMapBounds, setMapZoomLevel]);

  // Force marker clear on filtered charger changes to trigger full re-render
  useEffect(() => {
    if (!map || !isMapLoaded) return;
    markersRef.current.forEach(m => {
      m.setMap(null);
      // Also cleanup count overlay if present
      if (m.countOverlay) {
        m.countOverlay.setMap(null);
      }
    });
    markersRef.current = [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredChargers, map, isMapLoaded]); // Removed markersRef as it's a ref

  // Convert chargers to GeoJSON points - use filtered chargers when available
  const points = useMemo(() => {
    // Use filteredChargers from MapContext (properly filtered by the MapContext filter logic)
    const chargersToUse = filteredChargers && filteredChargers.length >= 0 ? filteredChargers : chargers;
    
    if (!chargersToUse || !chargersToUse.length) return [];
    
    
    return chargersToUse.map((charger, index) => {
      const lat = charger.location?.coordinates?.[1] || 
                  charger.locationLatitude || 
                  charger.lat || 
                  charger.latitude;
      const lng = charger.location?.coordinates?.[0] || 
                  charger.locationLongitude || 
                  charger.lng
      
      if (!lat || !lng) {
        return null;
      }
      
      return {
        type: 'Feature',
        properties: {
          cluster: false,
          chargerId: charger._id || charger.chargepoint_pk || charger.id || index,
          charger: charger
        },
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(lng), parseFloat(lat)]
        }
      };
    }).filter(Boolean);
  }, [chargers, filteredChargers]);

  // Use useSupercluster hook for proper clustering - completely disable during route
  const { clusters, supercluster } = useSupercluster({
    points: (routeData?.route || showRouteChargersOnly) ? [] : points, // No points during route = no clustering
    bounds: mapBounds,
    zoom: mapZoomLevel,
    options: { 
      radius: 170, // Keep normal clustering when not routing
      maxZoom: 16, 
      minPoints: 2 
    },
  });

  // Render charger markers with clustering using useSupercluster
  useEffect(() => {
    if (!map || !window.google || !isMapLoaded) {
      return;
    }

    // During route planning, render individual markers without clustering
    if (routeData?.route || showRouteChargersOnly) {
      
      // Add null check for routeData
      if (!routeData) {
        return;
      }
      
      // Clear existing markers
      markersRef.current.forEach(marker => {
        marker.setMap(null);
        // Also cleanup count overlay if present
        if (marker.countOverlay) {
          marker.countOverlay.setMap(null);
        }
      });
      markersRef.current = [];
      
      // For EV Advanced Route: Show only necessary charging stations (SOC calculation stops)
    // For Simple Route: Show all chargers along route
    let routeChargers = [];
    
    if (routeData?.mode === 'ev_advanced') {
      // EV Advanced: ONLY show charging stops that are calculated based on SOC/battery
      // Do NOT fall back to chargingStations
      routeChargers = routeData.chargingStops || [];
      
      // 🚨 HEURISTIC CHARGER REPLACEMENT LOGIC 🚨
      // If we detect heuristic chargers, replace them with real chargers from the map
      
      // Use the same charger source as Simple mode
      const availableChargers = mapChargers || filteredChargers || chargers || [];
      
      const hasHeuristicChargers = routeChargers.some(charger => {
        const isHeuristic = charger.heuristic === true || 
          (typeof charger.id === 'string' && charger.id.startsWith('heuristic_')) || 
          (typeof charger.name === 'string' && charger.name.includes('Heuristic Stop'));
        return isHeuristic;
      });
      
      
      if (hasHeuristicChargers && availableChargers && availableChargers.length > 0) {
        
        // Import the route filtering function to get chargers actually along the route
        let routeFilteredChargers = availableChargers;
        
        try {
          // Try to get route path for filtering
          if (routeData && routeData.route && routeData.route.routes && routeData.route.routes[0]) {
            const routePath = routeData.route.routes[0].overview_path;
            if (routePath && routePath.length > 0) {
              
              // Use the same filtering as Simple mode
              routeFilteredChargers = filterMapChargersAlongRoute(availableChargers, routePath, 8); // 8km buffer for EV
            }
          }
        } catch (filterError) {
        }
        
        // Replace heuristic chargers with real chargers along the route
        const replacementChargers = routeFilteredChargers.slice(0, Math.min(routeChargers.length, routeFilteredChargers.length));
        
        routeChargers = replacementChargers.map((realCharger, index) => ({
          // Basic identification
          id: realCharger.id || realCharger._id || `real_charger_${index}`,
          name: realCharger.name || realCharger.chargepoint_name || `Real Charger ${index + 1}`,
          chargepoint_name: realCharger.chargepoint_name || realCharger.name,
          
          // Coordinates
          lat: realCharger.locationLatitude || realCharger.lat || realCharger.latitude,
          lng: realCharger.locationLongitude || realCharger.lng || realCharger.longitude,
          latitude: realCharger.locationLatitude || realCharger.lat || realCharger.latitude,
          longitude: realCharger.locationLongitude || realCharger.lng || realCharger.longitude,
          locationLatitude: realCharger.locationLatitude || realCharger.lat || realCharger.latitude,
          locationLongitude: realCharger.locationLongitude || realCharger.lng || realCharger.longitude,
          location: realCharger.location || {
            coordinates: [
              realCharger.locationLongitude || realCharger.lng || realCharger.longitude,
              realCharger.locationLatitude || realCharger.lat || realCharger.latitude
            ]
          },
          
          // Address information
          address: realCharger.address || realCharger.street || realCharger.locationStreet,
          street: realCharger.street || realCharger.address || realCharger.locationStreet,
          city: realCharger.city || realCharger.locationCity,
          state: realCharger.state || realCharger.locationState,
          country: realCharger.country || realCharger.locationCountry,
          postalCode: realCharger.postalCode || realCharger.locationPostalCode || realCharger.zipCode,
          fullAddress: realCharger.fullAddress || realCharger.address,
          
          // Network and operator
          network: realCharger.network || realCharger.operator || realCharger.team_name || 'Real Network',
          operator: realCharger.operator || realCharger.network || realCharger.team_name,
          provider: realCharger.provider || realCharger.operator,
          owner: realCharger.owner || realCharger.operator,
          team_name: realCharger.team_name || realCharger.network,
          
          // Power and connector information
          power: realCharger.power || realCharger.maxPower || realCharger.powerRating || 50,
          maxPower: realCharger.maxPower || realCharger.power || realCharger.powerRating || 50,
          powerRating: realCharger.powerRating || realCharger.power || realCharger.maxPower || 50,
          
          // Connector data - preserve original connector structure
          connectors: realCharger.connectors || (realCharger.connector_types ? 
            realCharger.connector_types.map(type => ({
              connectorType: type,
              connectorName: type,
              type: type,
              power: realCharger.power || realCharger.maxPower || 50,
              status: 'Available'
            })) : [{
              connectorType: 'CCS Type 2',
              connectorName: 'CCS Type 2', 
              type: 'CCS Type 2',
              power: realCharger.power || realCharger.maxPower || 50,
              status: 'Available'
            }]
          ),
          connector_types: realCharger.connector_types || ['CCS Type 2'],
          chargerType: realCharger.connector_types?.[0] || 'CCS Type 2',
          
          // Status and availability
          status: realCharger.status || 'Available',
          availability: realCharger.availability || 'available',
          
          // Keep original charging calculation data if available
          arrivalSOC: routeChargers[index]?.arrivalSOC || 30,
          departureSOC: routeChargers[index]?.departureSOC || 80,
          chargingTime: routeChargers[index]?.chargingTime || 30,
          distanceFromStart: routeChargers[index]?.distanceFromStart || (index + 1) * 100,
          energyAdded: routeChargers[index]?.energyAdded || 40,
          heuristic: false, // Mark as real charger
          isRealCharger: true
        }));
        
        
        // CRITICAL: Update the original routeData so RouteDetailsPanel sees the real chargers
        if (routeData && routeData.chargingStops) {
          routeData.chargingStops = routeChargers;
        }
      }
    } else {
      // Simple Route: Show all chargers along route
      routeChargers = (routeData && routeData.chargingStations) ? routeData.chargingStations : [];
    }
      
        // Process route chargers
        for (const charger of routeChargers) {
          const lat = charger.location?.coordinates?.[1] || 
                      charger.locationLatitude || 
                      charger.lat || 
                      charger.latitude;
          const lng = charger.location?.coordinates?.[0] || 
                      charger.locationLongitude || 
                      charger.lng || 
                      charger.longitude;


          if (lat && lng) {
            const position = { lat: parseFloat(lat), lng: parseFloat(lng) };
            
            // Check for co-located chargers at this position
            const colocatedChargers = findColocatedChargers(charger, routeChargers);
            const colocatedCount = colocatedChargers.length;
            
            const marker = createCustomMarker(charger, position, map, colocatedCount, routeChargers);
            
            // Add click listener for charger info with co-location detection
            marker.addListener('click', () => {
              handleMarkerClick(charger, routeChargers);
            });

            // Add hover listeners
            marker.addListener('mouseover', () => {
              if (onChargerHover) {
                onChargerHover(charger);
              }
            });

            marker.addListener('mouseout', () => {
              if (onChargerMouseOut) {
                onChargerMouseOut();
              } else {
                // Fallback to old behavior if no specific handler
                setTimeout(() => {
                  if (onChargerHover) {
                    onChargerHover(null);
                  }
                }, 100);
              }
            });

            markersRef.current.push(marker);
          }
        }      return;
    }

    // Normal clustering when not routing
    if (!clusters?.length) {
      return;
    }


    // Clear existing markers
    markersRef.current.forEach(marker => {
      marker.setMap(null);
      // Also cleanup count overlay if present
      if (marker.countOverlay) {
        marker.countOverlay.setMap(null);
      }
    });
    markersRef.current = [];
    
    // Process clusters with async marker creation
    const markers = [];
    for (const cluster of clusters) {
      const [lng, lat] = cluster.geometry.coordinates;
      const { cluster: isCluster, point_count: pointCount } = cluster.properties;
  
      if (isCluster) {
        // Create cluster marker with 3D effect using custom SVG and smooth animations
        const clusterMarker = new window.google.maps.Marker({
          position: { lat, lng },
          map,
          icon: create3DClusterIcon(pointCount, points.length),
          zIndex: 1000 + pointCount, // Higher z-index for clusters
          animation: null, // No drop animation - start smooth
          optimized: false // Required for smooth animations
        });
        
        // Add smooth scale-in animation instead of drop
        setTimeout(() => {
          clusterMarker.setAnimation(window.google.maps.Animation.BOUNCE);
          setTimeout(() => {
            clusterMarker.setAnimation(null);
          }, 300); // Quick single bounce
        }, Math.random() * 200); // Staggered appearance
        
        // Add hover effects
        clusterMarker.addListener('mouseover', () => {
          // Subtle scale animation on hover
          const currentIcon = clusterMarker.getIcon();
          if (currentIcon && currentIcon.scaledSize) {
            const newSize = new window.google.maps.Size(
              currentIcon.scaledSize.width + 5,
              currentIcon.scaledSize.height + 5
            );
            clusterMarker.setIcon({
              ...currentIcon,
              scaledSize: newSize
            });
          }
        });
        
        clusterMarker.addListener('mouseout', () => {
          // Reset to original size
          clusterMarker.setIcon(create3DClusterIcon(pointCount, points.length));
        });
        
        // Add smooth zoom transition on click
        // Add smooth zoom transition on click with proper marker re-rendering
        clusterMarker.addListener('click', () => {
          try {
            const expansionZoom = supercluster.getClusterExpansionZoom(cluster.id);
            
            // Smooth transition without drop animation
            const targetZoom = Math.min(expansionZoom, 18);
            map.panTo({ lat, lng });
            
            // Gradual zoom for smoother effect
            const currentZoom = map.getZoom();
            const zoomDiff = targetZoom - currentZoom;
            const steps = Math.min(Math.abs(zoomDiff), 3);
            
            if (steps > 0) {
              const zoomStep = zoomDiff / steps;
              let currentStep = 0;
              
              const zoomInterval = setInterval(() => {
                currentStep++;
                const newZoom = currentZoom + (zoomStep * currentStep);
                map.setZoom(newZoom);
                
                if (currentStep >= steps) {
                  clearInterval(zoomInterval);
                  map.setZoom(targetZoom);
                  
                  // Force re-render of markers after zoom completes
                  setTimeout(() => {
                    const bounds = map.getBounds();
                    if (bounds) {
                      setMapBounds([
                        bounds.getSouthWest().lng(),
                        bounds.getSouthWest().lat(),
                        bounds.getNorthEast().lng(),
                        bounds.getNorthEast().lat()
                      ]);
                      setMapZoomLevel(targetZoom);
                    }
                  }, 200);
                }
              }, 150); // 150ms per step for smooth transition
            } else {
              // No zoom needed, but still force re-render
              setTimeout(() => {
                const bounds = map.getBounds();
                if (bounds) {
                  setMapBounds([
                    bounds.getSouthWest().lng(),
                    bounds.getSouthWest().lat(),
                    bounds.getNorthEast().lng(),
                    bounds.getNorthEast().lat()
                  ]);
                }
              }, 200);
            }
          } catch (error) {
            // Fallback with smooth animation
            const currentZoom = map.getZoom();
            map.panTo({ lat, lng });
            setTimeout(() => {
              map.setZoom(Math.min(currentZoom + 2, 18));
              // Force re-render after fallback zoom
              setTimeout(() => {
                const bounds = map.getBounds();
                if (bounds) {
                  setMapBounds([
                    bounds.getSouthWest().lng(),
                    bounds.getSouthWest().lat(),
                    bounds.getNorthEast().lng(),
                    bounds.getNorthEast().lat()
                  ]);
                }
              }, 200);
            }, 200);
          }
        });
        
        markers.push(clusterMarker);
      } else {
        // Individual charger marker
        const charger = cluster.properties.charger;
        const position = new window.google.maps.LatLng(lat, lng);
        
        // Check for co-located chargers at this position 
        const allChargers = chargers || filteredChargers || [];
        const colocatedChargers = findColocatedChargers(charger, allChargers);
        const colocatedCount = colocatedChargers.length;
        
        // Create custom marker with network icon, status dot, and co-location count
        const marker = createCustomMarker(charger, position, map, colocatedCount, allChargers);

        // Click handler with co-location detection
        marker.addListener('click', () => {
          // For clustered markers, use all available chargers as potential co-located set
          const allChargers = chargers || filteredChargers || [];
          handleMarkerClick(charger, allChargers);
        });

        // Hover handlers
        marker.addListener('mouseover', () => {
          if (onChargerHover) {
            onChargerHover(charger);
          }
        });

        marker.addListener('mouseout', () => {
          if (onChargerMouseOut) {
            onChargerMouseOut();
          } else {
            // Fallback to old behavior
            if (onChargerHover) {
              onChargerHover(null);
            }
          }
        });

        markers.push(marker);
      }
    }

    markersRef.current = markers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isMapLoaded, clusters, supercluster, routeData, showRouteChargersOnly, onChargerClick, onChargerHover]); // Reduced dependencies to avoid excessive re-renders

  // Render route polyline for new route system
  useEffect(() => {
    if (!map || !window.google || !routeData || !routeData.polyline) return;

    // Clear existing route polyline
    if (routePolylineRef.current) {
      routePolylineRef.current.setMap(null);
    }

    // Clear existing route markers
    routeMarkersRef.current.forEach(marker => marker.setMap(null));
    routeMarkersRef.current = [];

    // Create route polyline
    const polyline = new window.google.maps.Polyline({
      path: routeData.polyline.map(point => ({
        lat: point.lat,
        lng: point.lng
      })),
      geodesic: true,
      strokeColor: '#3b82f6',
      strokeOpacity: 1.0,
      strokeWeight: 4
    });

    polyline.setMap(map);
    routePolylineRef.current = polyline;

    // Add route markers (A, B, C, etc.)
    const markers = [];
    if (routeData.waypoints) {
      routeData.waypoints.forEach((waypoint, index) => {
        if (waypoint.coordinates) {
          const marker = new window.google.maps.Marker({
            position: waypoint.coordinates,
            map,
            title: waypoint.location,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: index === 0 ? '#22c55e' : index === routeData.waypoints.length - 1 ? '#3b82f6' : '#f59e0b',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2
            },
            label: {
              text: String.fromCharCode(65 + index),
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 'bold'
            }
          });
          markers.push(marker);
        }
      });
    }

    routeMarkersRef.current = markers;

    // Fit map to route bounds
    if (routeData.bounds) {
      map.fitBounds(routeData.bounds);
    }


    return () => {
      if (routePolylineRef.current) {
        routePolylineRef.current.setMap(null);
      }
      routeMarkersRef.current.forEach(marker => marker.setMap(null));
    };
  }, [map, routeData]);

  // Render route
  useEffect(() => {
    const routeToRender = routeData?.route || route;
    if (!map || !window.google || !routeToRender) {
      return;
    }


    // Clear existing route
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
    }

    // Render new route
    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: false, // Show default A/B markers
      polylineOptions: {
        strokeColor: '#4CAF50',
        strokeWeight: 6,
        strokeOpacity: 0.9,
      },
      markerOptions: {
        draggable: false
      }
    });

    directionsRenderer.setDirections(routeToRender);
    directionsRendererRef.current = directionsRenderer;

    // Fit map bounds to show the entire route
    try {
      if (routeToRender.routes && routeToRender.routes[0]) {
        const bounds = routeToRender.routes[0].bounds;
        if (bounds) {
          map.fitBounds(bounds);
        }
      }
    } catch (boundsError) {
    }


    return () => {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
    };
  }, [map, route, routeData, directionsRendererRef]);

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <GoogleMapReact
        bootstrapURLKeys={bootstrapKeys}
        center={mapCenter}
        zoom={mapZoom}
        onGoogleApiLoaded={handleApiLoaded}
        yesIWantToUseGoogleMapApiInternals
        options={mapOptions}
      />
    </Box>
  );
};

export default MapContainer;
